import _ from 'lodash';
import UriJs from 'urijs';

const utilities = {
  // Clean CSS Identifier name.
  cleanCssIdentifier(name) {
    return name
      .toString()
      .toLowerCase()
      .replace(/\W/g, '-')
      .replace(/_/g, '-');
  },
  objSort(obj) {
    const sorted = {};
    _.each(obj, (el, key) => {
      if (_.isArray(el)) {
        sorted[key] = el.sort();
      }
      else {
        sorted[key] = el;
      }
    });
    return sorted;
  },
  getNestedObject(nestedObj, pathArr) {
    return pathArr.reduce(
      (obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined),
      nestedObj,
    );
  },
  addListeners(element, events, handler) {
    events.forEach((event) => {
      element.addEventListener(event, handler);
    });
  },
  removeListeners(element, events, handler) {
    events.forEach((event) => {
      element.removeEventListener(event, handler);
    });
  },
  getParameterByName(name, url) {
    if (!url) url = window.location.href;
    name = name.replace(/[[]]/g, '\\$&');
    const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);

    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  },
  stripTags(text) {
    return text.replace(/<(.|\n)*?>/g, '');
  },
  /**
   * Deep diff between two object, using lodash
   * @param {Object} object       Object compared
   * @param {Object} base         Object to compare with
   * @param {boolean} arrayDiff   Show differences for arrays.
   * @param {Array} exceptions    List of fields which don't need to check
   * @return {Object}             Return a new object who represent the diff
   */
  difference(object, base, arrayDiff = false, exceptions = []) {
    function changes(innerObject, innerBase) {
      return _.transform(innerObject, (result, value, key) => {
        if (exceptions.indexOf(key) === -1) {
          if (!_.isEqual(value, innerBase[key])) {
            if (arrayDiff && _.isArray(value) && _.isArray(innerBase[key])) {
              result[key] = _.difference(value, innerBase[key]);
            }
            else if (_.isObject(value) && _.isObject(innerBase[key])) {
              result[key] = changes(value, innerBase[key]);
            }
            else {
              result[key] = value;
            }
          }
        }
      });
    }
    return changes(object, base);
  },

  /**
   * Check if user is logged in.
   *
   * @param {Object} userdData
   *   User data object in redux store.
   * @returns {*|boolean}
   */
  isLoggedIn(userData) {
    return userData && !!parseInt(userData.uid, 10);
  },

  /**
   * Generate random hash.
   *
   * @returns {string}
   */
  randomHash() {
    return Math.random()
      .toString(36)
      .substr(2, 9);
  },

  /**
   * Handler for ESC keydown.
   *
   * @param callback
   *   Optional callback function.
   */
  escapeHandler(e, callback) {
    let isEscape = false;
    if ('key' in e) {
      isEscape = e.key === 'Escape' || e.key === 'Esc';
    }
    else {
      isEscape = e.keyCode === 27;
    }
    if (isEscape && typeof callback === 'function') {
      callback();
    }
  },

  /**
   * Get paths parts based on hash string.
   *
   * @param {string} path
   *   Hash string.
   *
   * @returns {{pathes: [], anchoredTextId: *}}
   */
  getPathParts(path) {
    const anchorRegex = /&anchor=(.[^&]+)/;
    const anchorMatches = path.match(anchorRegex);
    const anchoredTextId = anchorMatches && anchorMatches[1];
    const pathes = path
      .replace('#', '')
      .replace(anchorRegex, '')
      .replace(new RegExp(encodeURI('\\'), 'g'), '/')
      .replace(new RegExp(/\\/, 'g'), '/')
      .split('/')
      .filter(item => item !== '');
    return {pathes, anchoredTextId};
  },

  /**
   * Check if current page is map.
   *
   * @param history
   *   History object.
   * @param path
   *   Url path.
   *
   * @returns {boolean}
   */
  isMapPage(history, path = '') {
    if (process.env.NODE_ENV !== 'development') {
      return (
        window !== 'undefined'
        && `${window.location.protocol}//${window.location.host}` === process.env.REACT_APP_MAP_URL
      );
    }
    const pathname = path || history.location.pathname;
    return pathname.indexOf('/map') > -1;
  },

  /**
   * Check if this front page.
   *
   * @param {*} location
   *   Location object.
   */
  isFront(location) {
    return location.pathname === '/';
  },

  /**
   * Get currest state param.
   *
   * @returns {string}
   *   Current city state.
   */
  getCurrentStateEnv() {
    // @todo Doesn't matter which state to specify, it's an orphan required parameter.
    return 'fl';
  },

  /**
   * Prepare request url.
   *
   * @param {*} type
   *   Request type.
   * @param {*} action
   *   Action name.
   * @param {*} extraParams
   *   Other get params.
   * @param {bool} asObject
   *   Return object instead of string.
   */
  prepareApiUrl(type = '', action = '', extraParams = {}, asObject = false) {
    const url = new UriJs(`${process.env.REACT_APP_API_URL}/api/ui-api`)
      .search('?rows=100&offset=0&fields[]=title')
      .addSearch('type', type)
      .addSearch('action', action)
      .addSearch('state_env', utilities.getCurrentStateEnv());
    _.each(extraParams, (value, name) => {
      url.setSearch(name, value);
    });
    if (asObject) {
      return url;
    }
    return url.normalizeSearch().readable();
  },

  /**
   * Get destination url for UMT.
   *
   * @param urlType
   *   Type of request to UMT (login/register/logout).
   * @param url
   *   Url.
   * @returns {string}
   *   Destination encoded url.
   */
  getDestinationUrl(urlType, url) {
    const hash = window.location.hash.replace('#', '');
    const destUrl = `${window.location.protocol}//${window.location.host}${
      window.location.pathname
    }${encodeURIComponent(window.location.search)}`;
    switch (urlType) {
      case 'register':
        return `${process.env.REACT_APP_ACCOUNTS_SITE}/user/register?app=propzone&anchor=${hash}&destination-url=${destUrl}`;
      case 'logout':
        return `${process.env.REACT_APP_ACCOUNTS_SITE}/user/logout?app=propzone&anchor=${hash}&destination-url=${destUrl}`;
      default:
        if (urlType === 'subscribe' && url) {
          return `${url}&anchor=${hash}&destination-url=${destUrl}`;
        }
        return `${process.env.REACT_APP_ACCOUNTS_SITE}/user/login?app=propzone&anchor=${hash}&destination-url=${destUrl}`;
    }
  },

  thousandSeparator(value, separator = ',') {
    return value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator) : value;
  },

  isValidNumber(str = '') {
    // eslint-disable-next-line no-restricted-globals
    return !isNaN(typeof str === 'string' ? +str?.replaceAll(',', '').replaceAll('.', '') : str);
  },
};

export default utilities;
